import { useState } from 'react';
import type { ReactElement } from 'react';

import { useUpdateProjectMutation } from 'features/master/api/master.api';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import {
  DEFAULT_DS19_MAX_PROPERTY_VALUE_UF,
  MAX_DS19_PROPERTY_VALUE_CEILING_UF,
  buildSubsidyConfigField,
  parseUFInput,
} from '../model/mortgageRules';

interface Props {
  projectId: number;
  projectName: string;
  /** The sub-project's DS19 amount in UF, or null when it has none. */
  amountUF: number | null;
  /** The sub-project's DS19 property-value ceiling in UF, or null when it runs on the default. */
  maxPropertyValueUF: number | null;
}

const formatUF = (amountUF: number): string => `${amountUF.toLocaleString('es-CL')} UF`;

/*
 * The DS19 amount of one sub-project, edited in its own row. It lives on the project
 * (`subsidyConfig`), not in any rule the bulk actions write, because it belongs to the project's
 * units: there is no way to copy it across a selection. The editor opens in a popover so the column
 * keeps its width and the table does not reflow, or scroll the controls out of view, while editing.
 */
const MortgageDs19SubsidyCell = ({ projectId, projectName, amountUF, maxPropertyValueUF }: Props): ReactElement => {
  const [updateProject, { isLoading: isSaving }] = useUpdateProjectMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [ceilingInput, setCeilingInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const startEditing = (): void => {
    setAmountInput(amountUF === null ? '' : String(amountUF));
    setCeilingInput(maxPropertyValueUF === null ? '' : String(maxPropertyValueUF));
    setError(null);
    setIsEditing(true);
  };

  const save = async (): Promise<void> => {
    const amount = parseUFInput(amountInput, MAX_DS19_PROPERTY_VALUE_CEILING_UF);
    if ('error' in amount) {
      setError(`Monto: ${amount.error}`);
      return;
    }
    const ceiling = parseUFInput(ceilingInput, MAX_DS19_PROPERTY_VALUE_CEILING_UF);
    if ('error' in ceiling) {
      setError(`Tope: ${ceiling.error}`);
      return;
    }
    if (amount.valueUF === amountUF && ceiling.valueUF === maxPropertyValueUF) {
      setIsEditing(false);
      return;
    }

    setError(null);

    try {
      await updateProject({
        id: projectId,
        patch: { subsidyConfig: buildSubsidyConfigField(amount.valueUF, ceiling.valueUF) },
      }).unwrap();
      setIsEditing(false);
    } catch (saveError) {
      setError(extractErrorMessage(saveError, 'No se pudo guardar el subsidio DS19.'));
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Fixed width so the button lines up down the column whatever the amount reads. */}
      <div className="w-24 text-sm tabular-nums">
        <p className={cn('whitespace-nowrap', amountUF === null && 'text-text-muted')}>
          {amountUF === null ? 'Sin monto' : formatUF(amountUF)}
        </p>
        <p className="whitespace-nowrap text-xs text-text-muted">
          {`Tope ${formatUF(maxPropertyValueUF ?? DEFAULT_DS19_MAX_PROPERTY_VALUE_UF)}`}
        </p>
      </div>
      <Popover
        open={isEditing}
        onOpenChange={(open) => {
          if (isSaving) return;
          if (open) startEditing();
          else setIsEditing(false);
        }}
      >
        <PopoverTrigger asChild>
          <Button type="button" size="xs" variant="ghost">
            Editar
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64">
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <div className="space-y-1">
              <p className="text-sm font-medium">Subsidio DS19</p>
              <p className="text-xs text-text-muted">{projectName}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`ds19-${projectId}`} className="text-xs">
                Monto
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id={`ds19-${projectId}`}
                  autoFocus
                  inputMode="decimal"
                  className="h-8"
                  placeholder="Sin monto"
                  value={amountInput}
                  disabled={isSaving}
                  aria-invalid={error?.startsWith('Monto') === true}
                  onChange={(event) => setAmountInput(event.target.value)}
                />
                <span className="text-sm text-text-muted">UF</span>
              </div>
              <p className="text-xs text-text-muted">{`Hasta ${MAX_DS19_PROPERTY_VALUE_CEILING_UF} UF. Déjalo vacío para quitarlo.`}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`ds19-ceiling-${projectId}`} className="text-xs">
                Tope valor propiedad
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id={`ds19-ceiling-${projectId}`}
                  inputMode="decimal"
                  className="h-8"
                  placeholder={String(DEFAULT_DS19_MAX_PROPERTY_VALUE_UF)}
                  value={ceilingInput}
                  disabled={isSaving}
                  aria-invalid={error?.startsWith('Tope') === true}
                  onChange={(event) => setCeilingInput(event.target.value)}
                />
                <span className="text-sm text-text-muted">UF</span>
              </div>
              <p className="text-xs text-text-muted">{`Hasta ${MAX_DS19_PROPERTY_VALUE_CEILING_UF} UF. Déjalo vacío para usar ${DEFAULT_DS19_MAX_PROPERTY_VALUE_UF} UF.`}</p>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="outline" disabled={isSaving} onClick={() => setIsEditing(false)}>
                Cancelar
              </Button>
              <Button type="submit" size="sm" disabled={isSaving}>
                {isSaving ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MortgageDs19SubsidyCell;
